import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Table,
  Tag,
  Button,
  Input,
  Select,
  Space,
  Modal,
  Alert,
  Typography,
  Popconfirm,
  InputNumber,
  Collapse,
  Checkbox,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  KeyOutlined,
  DeleteOutlined,
  LoginOutlined,
  GiftOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { api, HttpError, Role, User, QuotaLedgerRow } from '../api';
import { t } from '../i18n';
import { fmtDateTime } from '../time';

function fmtTs(unix: number | null): string {
  if (!unix) return '—';
  return fmtDateTime(unix);
}
const nowUnix = () => Math.floor(Date.now() / 1000);

type Grant = Awaited<ReturnType<typeof api.listQuotas>>[number];

/** Read-only role chip. superadmin is gold to stand out as the top tier. */
function RoleTag({ role }: { role: Role }) {
  const map: Record<Role, { color: string; label: string }> = {
    superadmin: { color: 'gold', label: t('userRoleSuperadmin') },
    admin: { color: 'blue', label: t('userRoleAdmin') },
    client: { color: 'default', label: t('userRoleClient') },
  };
  return <Tag color={map[role].color}>{map[role].label}</Tag>;
}

export function Users() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [resetFor, setResetFor] = useState<{ id: string; email: string } | null>(null);
  const [grantFor, setGrantFor] = useState<{ id: string; email: string } | null>(null);
  const [revokeFor, setRevokeFor] = useState<{ id: string; email: string; available: number } | null>(null);
  const [editFor, setEditFor] = useState<User | null>(null);
  const [impersonateFor, setImpersonateFor] = useState<User | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Share the ['me'] cache with App, but match its error semantics: only a 401
  // means "logged out" (→ null); any other error is rethrown so a transient 500
  // during a refetch never nulls the cache and logs the admin out.
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (e) {
        if (e instanceof HttpError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
  const q = useQuery({ queryKey: ['users'], queryFn: api.listUsers });
  const quotasQ = useQuery({ queryKey: ['quotas'], queryFn: () => api.listQuotas(), refetchInterval: 10_000 });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['quotas'] });
    qc.invalidateQueries({ queryKey: ['quota-ledger'] });
  };
  const fail = (e: Error) => setMsg({ ok: false, text: `${t('userActionFailed')}: ${e.message}` });

  const grants = quotasQ.data ?? [];
  const quotaByUser = new Map<string, { available: number; total: number; used: number; grants: Grant[] }>();
  for (const g of grants) {
    const agg = quotaByUser.get(g.clientUserId) ?? { available: 0, total: 0, used: 0, grants: [] as Grant[] };
    const expired = g.expiresAt !== null && g.expiresAt < nowUnix();
    if (!expired) agg.available += Math.max(0, g.totalCount - g.usedCount);
    agg.total += g.totalCount;
    agg.used += g.usedCount;
    agg.grants.push(g);
    quotaByUser.set(g.clientUserId, agg);
  }

  const create = useMutation({
    mutationFn: (v: { username: string; password: string; role: Role; email?: string; note?: string }) =>
      api.createUser({
        username: v.username.trim(),
        password: v.password,
        role: v.role,
        email: v.email?.trim() || undefined,
        note: v.note?.trim() || undefined,
      }),
    onSuccess: (r) => {
      setMsg({ ok: true, text: `${r.username} (${r.role})` });
      setCreateOpen(false);
      invalidate();
    },
    onError: fail,
  });
  const resetPw = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => api.updateUser(id, { password }),
    onSuccess: (_r, vars) => {
      const u = (q.data ?? []).find((x) => x.id === vars.id);
      setMsg({ ok: true, text: t('userResetPwOk', { email: u?.username ?? vars.id }) });
      setResetFor(null);
    },
    onError: fail,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => { setMsg(null); invalidate(); },
    onError: fail,
  });
  const editM = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { username?: string; email?: string | null; role?: Role; password?: string; note?: string | null } }) =>
      api.updateUser(id, patch),
    onSuccess: (_r, vars) => {
      const u = (q.data ?? []).find((x) => x.id === vars.id);
      setMsg({ ok: true, text: t('userEditOk', { email: vars.patch.username ?? u?.username ?? vars.id }) });
      setEditFor(null);
      invalidate();
    },
    onError: fail,
  });
  const grant = useMutation({
    mutationFn: (opts: { clientUserId: string; totalCount: number; expiresAt?: number; note?: string }) => api.grantQuota(opts),
    onSuccess: (r, vars) => {
      const u = (q.data ?? []).find((x) => x.id === vars.clientUserId);
      setMsg({ ok: true, text: t('quotaGrantOk', { email: u?.username ?? vars.clientUserId, available: r.available }) });
      setGrantFor(null);
      invalidate();
    },
    onError: fail,
  });
  const revoke = useMutation({
    mutationFn: (opts: { clientUserId: string; amount: number; note?: string }) => api.revokeQuota(opts),
    onSuccess: (r, vars) => {
      const u = (q.data ?? []).find((x) => x.id === vars.clientUserId);
      setMsg({ ok: true, text: t('quotaRevokeOk', { email: u?.username ?? vars.clientUserId, available: r.available }) });
      setRevokeFor(null);
      invalidate();
    },
    onError: fail,
  });

  const users = q.data ?? [];
  const myId = me.data?.user.id;
  const isSuper = me.data?.user.role === 'superadmin';
  // Only treat a row as "self" when we actually know our own id — a null myId
  // (me fetch failed) must NOT unlock delete/demote on the admin's own row.
  const isSelf = (id: string): boolean => !!myId && id === myId;
  const selfUnknown = !myId;
  // A regular admin may not touch a superadmin account (backend enforces 403).
  const canManage = (u: User): boolean => isSuper || u.role !== 'superadmin';

  const columns: ColumnsType<User> = [
    {
      title: t('userUsername'),
      dataIndex: 'username',
      render: (username: string, u) => (
        <Space>
          <div>
            <div>{username}</div>
            {u.email && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {u.email}
              </Typography.Text>
            )}
          </div>
          {isSelf(u.id) && <Tag>{t('userYouTag')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('userRole'),
      dataIndex: 'role',
      // Role is a read-only chip here; it's changed in the Edit modal (superadmin
      // only). Keeps a destructive privilege change behind an explicit dialog.
      render: (role: Role) => <RoleTag role={role} />,
    },
    {
      title: t('userQuota'),
      key: 'quota',
      render: (_: unknown, u) => {
        if (u.role !== 'client') return <Typography.Text type="secondary">—</Typography.Text>;
        const quota = quotaByUser.get(u.id);
        return (
          <Space wrap>
            <Tag color={quota && quota.available > 0 ? 'green' : 'orange'}>{quota?.available ?? 0}</Tag>
            <Button size="small" icon={<GiftOutlined />} onClick={() => setGrantFor({ id: u.id, email: u.username })}>
              {t('quotaGrantBtn')}
            </Button>
            <Button
              size="small"
              danger
              icon={<MinusCircleOutlined />}
              disabled={(quota?.available ?? 0) <= 0}
              onClick={() => setRevokeFor({ id: u.id, email: u.username, available: quota?.available ?? 0 })}
            >
              {t('quotaRevokeBtn')}
            </Button>
          </Space>
        );
      },
    },
    {
      title: t('userNote'),
      dataIndex: 'note',
      render: (n: string | null) =>
        n ? (
          <Typography.Text style={{ maxWidth: 220 }} ellipsis={{ tooltip: n }}>
            {n}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    { title: t('userCreated'), dataIndex: 'createdAt', render: (v: number | null) => <Typography.Text type="secondary">{fmtTs(v)}</Typography.Text> },
    {
      title: t('colActions'),
      key: 'actions',
      render: (_: unknown, u) => (
        <Space wrap>
          {/* Sign in as: any admin can impersonate a client; only a superadmin can
              also impersonate a (regular) admin. Never a superadmin, never self. */}
          {!isSelf(u.id) &&
            (u.role === 'client' || (u.role === 'admin' && isSuper)) && (
              <Button size="small" icon={<LoginOutlined />} onClick={() => setImpersonateFor(u)}>
                {t('userImpersonate')}
              </Button>
            )}
          {canManage(u) && (
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditFor(u)}>
              {t('userEdit')}
            </Button>
          )}
          {canManage(u) && (
            <Button size="small" icon={<KeyOutlined />} onClick={() => setResetFor({ id: u.id, email: u.username })}>
              {t('userResetPw')}
            </Button>
          )}
          {!isSelf(u.id) && !selfUnknown && canManage(u) && (
            <Popconfirm title={t('userDeleteConfirm', { email: u.username })} onConfirm={() => del.mutate(u.id)}>
              <Button danger size="small" icon={<DeleteOutlined />} loading={del.isPending}>
                {t('userDelete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {msg && (
        <Alert type={msg.ok ? 'success' : 'error'} showIcon message={msg.text} closable onClose={() => setMsg(null)} />
      )}
      <Card
        title={t('usersTitle')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('userNewBtn')}
          </Button>
        }
      >
        <Table<User>
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={users}
          loading={q.isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('emptyList') }}
          expandable={{
            rowExpandable: (u) => (quotaByUser.get(u.id)?.grants.length ?? 0) > 0,
            expandedRowRender: (u) => <QuotaHistory grants={quotaByUser.get(u.id)?.grants ?? []} clientUserId={u.id} />,
          }}
        />
      </Card>

      {resetFor && (
        <ResetPasswordModal
          email={resetFor.email}
          pending={resetPw.isPending}
          onCancel={() => setResetFor(null)}
          onSubmit={(pw) => resetPw.mutate({ id: resetFor.id, password: pw })}
        />
      )}
      {grantFor && (
        <GrantQuotaModal
          email={grantFor.email}
          pending={grant.isPending}
          onCancel={() => setGrantFor(null)}
          onSubmit={(count, days, note) =>
            grant.mutate({
              clientUserId: grantFor.id,
              totalCount: count,
              expiresAt: days ? nowUnix() + days * 86400 : undefined,
              note: note || undefined,
            })
          }
        />
      )}
      {revokeFor && (
        <RevokeQuotaModal
          email={revokeFor.email}
          available={revokeFor.available}
          pending={revoke.isPending}
          onCancel={() => setRevokeFor(null)}
          onSubmit={(amount, note) =>
            revoke.mutate({
              clientUserId: revokeFor.id,
              amount,
              note: note || undefined,
            })
          }
        />
      )}
      {createOpen && (
        <CreateUserModal
          isSuper={isSuper}
          pending={create.isPending}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(username, password, role, email, note) =>
            create.mutate({ username, password, role, email, note })
          }
        />
      )}
      {editFor && (
        <EditUserModal
          user={editFor}
          canEditRole={isSuper && !isSelf(editFor.id)}
          pending={editM.isPending}
          onCancel={() => setEditFor(null)}
          onSubmit={(patch) => editM.mutate({ id: editFor.id, patch })}
        />
      )}
      {impersonateFor && (
        <ImpersonateModal
          user={impersonateFor}
          onCancel={() => setImpersonateFor(null)}
          onConfirm={async (block) => {
            await api.impersonate(impersonateFor.id, { block });
            window.location.href = '/';
          }}
        />
      )}
    </Space>
  );
}

function ImpersonateModal({
  user,
  onCancel,
  onConfirm,
}: {
  user: User;
  onCancel: () => void;
  onConfirm: (block: boolean) => Promise<void>;
}) {
  const [block, setBlock] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open
      title={t('impersonateConfirmTitle')}
      onCancel={onCancel}
      okText={t('impersonateConfirmOk')}
      okButtonProps={{ loading: busy, icon: <LoginOutlined /> }}
      onOk={async () => {
        setBusy(true);
        try {
          await onConfirm(block);
        } finally {
          setBusy(false);
        }
      }}
    >
      <Typography.Paragraph>{t('impersonateConfirmBody', { name: user.username })}</Typography.Paragraph>
      {/* "Block the account while I work" applies only to client accounts. */}
      {user.role === 'client' && (
        <Checkbox checked={block} onChange={(e) => setBlock(e.target.checked)}>
          {t('impersonateBlockLabel')}
        </Checkbox>
      )}
    </Modal>
  );
}

function EditUserModal({
  user,
  canEditRole,
  pending,
  onCancel,
  onSubmit,
}: {
  user: User;
  /** Only a superadmin (and never on their own row) may change a role. */
  canEditRole: boolean;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (patch: { username?: string; email?: string | null; role?: Role; password?: string; note?: string | null }) => void;
}) {
  const [username, setUsername] = useState(user.username);
  const [email, setEmail] = useState(user.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(user.role);
  const [note, setNote] = useState(user.note ?? '');
  const trimmedUser = username.trim();
  const trimmedEmail = email.trim();
  const trimmedNote = note.trim();
  // Only send fields that actually changed; a blank password means "keep".
  const patch: { username?: string; email?: string | null; role?: Role; password?: string; note?: string | null } = {};
  if (trimmedNote !== (user.note ?? '')) patch.note = trimmedNote || null;
  if (trimmedUser && trimmedUser !== user.username) patch.username = trimmedUser;
  if (trimmedEmail !== (user.email ?? '')) patch.email = trimmedEmail || null;
  if (canEditRole && role !== user.role) patch.role = role;
  if (password) patch.password = password;
  const valid =
    trimmedUser.length >= 2 &&
    (password === '' || password.length >= 8) &&
    Object.keys(patch).length > 0;
  return (
    <Modal
      open
      title={t('userEditTitle')}
      onCancel={onCancel}
      okText={t('userSave')}
      okButtonProps={{ disabled: !valid, loading: pending }}
      onOk={() => valid && onSubmit(patch)}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('userUsername')}</Typography.Text>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('userEmailOptional')}</Typography.Text>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('userRole')}</Typography.Text>
          {canEditRole ? (
            <Select
              value={role}
              onChange={setRole}
              style={{ width: '100%' }}
              options={[
                { value: 'client', label: t('userRoleClient') },
                { value: 'admin', label: t('userRoleAdmin') },
                { value: 'superadmin', label: t('userRoleSuperadmin') },
              ]}
            />
          ) : (
            <div><RoleTag role={user.role} /></div>
          )}
        </div>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('userNote')}</Typography.Text>
          <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('userNotePlaceholder')} autoSize={{ minRows: 2, maxRows: 4 }} />
        </div>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('userEditPasswordHint')}</Typography.Text>
          <Input.Password value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('userPasswordPlaceholder')} />
        </div>
      </Space>
    </Modal>
  );
}

function CreateUserModal({
  isSuper,
  pending,
  onCancel,
  onSubmit,
}: {
  isSuper: boolean;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (username: string, password: string, role: Role, email?: string, note?: string) => void;
}) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('client');
  const [note, setNote] = useState('');
  // Only a superadmin may create admins/superadmins; an admin creates clients.
  const roleOptions = isSuper
    ? [
        { value: 'client', label: t('userRoleClient') },
        { value: 'admin', label: t('userRoleAdmin') },
        { value: 'superadmin', label: t('userRoleSuperadmin') },
      ]
    : [{ value: 'client', label: t('userRoleClient') }];
  const valid = username.trim().length >= 2 && password.length >= 8;
  return (
    <Modal
      open
      title={t('userCreateTitle')}
      onCancel={onCancel}
      okText={t('userCreateBtn')}
      okButtonProps={{ disabled: !valid, loading: pending }}
      onOk={() => valid && onSubmit(username, password, role, email.trim() || undefined, note.trim() || undefined)}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input placeholder={t('userUsername')} value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        <Input type="email" placeholder={t('userEmailOptional')} value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input.Password placeholder={t('userPasswordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)} />
        <Select value={role} onChange={setRole} style={{ width: '100%' }} options={roleOptions} />
        <Input.TextArea placeholder={t('userNotePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} autoSize={{ minRows: 2, maxRows: 4 }} />
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('userPasswordMinHint')}</Typography.Text>
      </Space>
    </Modal>
  );
}

function QuotaHistory({ grants, clientUserId }: { grants: Grant[]; clientUserId: string }) {
  // Revocations aren't visible in the grants (they shrink existing grant totals);
  // pull them from the ledger (type 'adjust', negative delta) so an admin can see
  // what was taken back and why.
  const ledgerQ = useQuery({
    queryKey: ['quota-ledger', clientUserId],
    queryFn: () => api.quotaLedger({ clientUserId, limit: 200 }),
  });
  const revocations = (ledgerQ.data?.rows ?? []).filter((r) => r.type === 'adjust' && r.delta < 0);
  const revokeColumns: ColumnsType<QuotaLedgerRow> = [
    { title: t('historyWhen'), dataIndex: 'createdAt', render: (v: number) => <Typography.Text type="secondary">{fmtTs(v)}</Typography.Text> },
    { title: t('quotaRevokedAmount'), key: 'amt', render: (_: unknown, r) => <Tag color="volcano">−{-r.delta}</Tag> },
    { title: t('quotaRevokedBy'), dataIndex: 'actorUsername', render: (v: string | null) => v ?? '—' },
    { title: t('quotaNote'), dataIndex: 'reason', render: (n: string | null) => n ?? '—' },
  ];
  const columns: ColumnsType<Grant> = [
    { title: t('quotaTotal'), dataIndex: 'totalCount' },
    { title: t('quotaUsed'), dataIndex: 'usedCount' },
    {
      title: t('quotaRemaining'),
      key: 'remaining',
      render: (_: unknown, g) => {
        const remaining = g.totalCount - g.usedCount;
        const expired = g.expiresAt !== null && g.expiresAt < nowUnix();
        return <Tag color={expired ? 'default' : remaining > 0 ? 'green' : 'orange'}>{remaining}</Tag>;
      },
    },
    { title: t('quotaExpires'), dataIndex: 'expiresAt', render: (v: number | null) => <Typography.Text type="secondary">{fmtTs(v)}</Typography.Text> },
    { title: t('quotaGranted'), dataIndex: 'createdAt', render: (v: number) => <Typography.Text type="secondary">{fmtTs(v)}</Typography.Text> },
    {
      title: t('quotaGrantedBy'),
      dataIndex: 'grantedByUsername',
      render: (_: string | null, g) =>
        g.grantedByUsername ?? g.grantedByEmail ?? (g.grantedByUserId ? `${g.grantedByUserId.substring(0, 8)}…` : '—'),
    },
    { title: t('quotaNote'), dataIndex: 'note', render: (n: string | null) => n ?? '—' },
  ];
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Table<Grant> size="small" rowKey="id" columns={columns} dataSource={grants} pagination={false} scroll={{ x: 'max-content' }} />
      {revocations.length > 0 && (
        <div>
          <Typography.Text type="secondary" strong>{t('quotaRevocationsTitle')}</Typography.Text>
          <Table<QuotaLedgerRow>
            size="small"
            rowKey="id"
            columns={revokeColumns}
            dataSource={revocations}
            pagination={false}
            scroll={{ x: 'max-content' }}
            style={{ marginTop: 4 }}
          />
        </div>
      )}
    </Space>
  );
}

function GrantQuotaModal({
  email,
  pending,
  onCancel,
  onSubmit,
}: {
  email: string;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (count: number, days: number | null, note: string) => void;
}) {
  const [count, setCount] = useState<number | null>(1);
  const [days, setDays] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const valid = (count ?? 0) >= 1;
  return (
    <Modal
      open
      title={t('quotaGrantTitle')}
      onCancel={onCancel}
      okText={t('quotaSubmit')}
      okButtonProps={{ disabled: !valid, loading: pending }}
      onOk={() => valid && onSubmit(count ?? 1, days, note)}
    >
      <Typography.Paragraph type="secondary">{email}</Typography.Paragraph>
      <Space wrap>
        <InputNumber min={1} placeholder={t('quotaCount')} value={count} onChange={setCount} style={{ width: 110 }} autoFocus />
        <InputNumber min={1} placeholder={t('quotaExpiry')} value={days} onChange={setDays} style={{ width: 180 }} />
        <Input placeholder={t('quotaNote')} value={note} onChange={(e) => setNote(e.target.value)} style={{ width: 200 }} />
      </Space>
    </Modal>
  );
}

function RevokeQuotaModal({
  email,
  available,
  pending,
  onCancel,
  onSubmit,
}: {
  email: string;
  available: number;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (amount: number, note: string) => void;
}) {
  const [amount, setAmount] = useState<number | null>(1);
  const [note, setNote] = useState('');
  // Clamp policy mirrors the server: never revoke more than the balance.
  const valid = (amount ?? 0) >= 1 && (amount ?? 0) <= available;
  return (
    <Modal
      open
      title={t('quotaRevokeTitle')}
      onCancel={onCancel}
      okText={t('quotaRevokeSubmit')}
      okButtonProps={{ danger: true, disabled: !valid, loading: pending }}
      onOk={() => valid && onSubmit(amount ?? 1, note)}
    >
      <Typography.Paragraph type="secondary">{email}</Typography.Paragraph>
      <Typography.Paragraph type="secondary">{t('quotaRevokeAvailable', { available })}</Typography.Paragraph>
      <Space wrap>
        <InputNumber min={1} max={available} placeholder={t('quotaCount')} value={amount} onChange={setAmount} style={{ width: 110 }} autoFocus />
        <Input placeholder={t('quotaNote')} value={note} onChange={(e) => setNote(e.target.value)} style={{ width: 200 }} />
      </Space>
    </Modal>
  );
}

function ResetPasswordModal({
  email,
  pending,
  onCancel,
  onSubmit,
}: {
  email: string;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (password: string) => void;
}) {
  const [pw, setPw] = useState('');
  return (
    <Modal
      open
      title={t('userResetPw')}
      onCancel={onCancel}
      okText={t('userResetPw')}
      okButtonProps={{ disabled: pw.length < 8, loading: pending }}
      onOk={() => pw.length >= 8 && onSubmit(pw)}
    >
      <Typography.Paragraph type="secondary">{t('userResetPwPrompt', { email })}</Typography.Paragraph>
      <Input.Password autoFocus placeholder={t('userPasswordPlaceholder')} value={pw} onChange={(e) => setPw(e.target.value)} onPressEnter={() => pw.length >= 8 && onSubmit(pw)} />
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('userPasswordMinHint')}</Typography.Text>
    </Modal>
  );
}
